import { get as getByKey } from 'lodash';

import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';
import { RESET_ALL as RESET_ON_LOGOUT, danger } from 'redux-store/reducers/common-reducer';

import { createReducer } from 'utils/redux';
import { API, destroy, get, post } from 'utils/api';
import { sortOldestFirst } from 'utils/list';
import { t } from 'utils/language';

const RECEIVE = 'teamMembers/RECEIVE';
const RESET = 'teamMembers/RESET';

const { getActiveInvitation, ORGANIZATIONS, ADMIN_ORGANIZATIONS } = API.END_POINTS.API;
const COMMON_ERRORS = {
  'quota.exceeded.error': t('invite_team_members_quota_exceed_error'),
  'model.organization.lastMember': t('leave_team_last_member_error'),
  'model.organization.limitReached': t('team_limit_reached'),
  'model.organization.duplicateMember': t('same_team_member_error')
};

import type { TeamMemberOrgIdMap, TeamMembersState, ThunkAction } from 'types';

const initialState: TeamMembersState = {
  mapByOrganizationId: {} as TeamMemberOrgIdMap
};

function receiveTeamMembers(organizationId, data) {
  return {
    type: RECEIVE,
    payload: {
      organizationId,
      data
    }
  };
}

export function resetTeamMembers(organizationId) {
  return {
    type: RESET,
    payload: {
      organizationId
    }
  };
}

export function fetchTeamMembers(organizationId, options = { showLoader: true }) {
  const url = `${ORGANIZATIONS}/${organizationId}/members`;

  return (dispatch) => {
    if (options.showLoader) {
      dispatch(resetTeamMembers(organizationId));
      dispatch(showLoading(url));
    }

    return get(url, {}, { suppressGlobalNotification: true })
      .then((response) => {
        dispatch(receiveTeamMembers(organizationId, response.data));
        if (options.showLoader) {
          dispatch(hideLoading(url));
        }
      })
      .catch(() => {
        if (options.showLoader) {
          dispatch(hideLoading(url));
        }
      });
  };
}

export function removeTeamMember(organizationId, memberId) {
  const url = `${ORGANIZATIONS}/${organizationId}/members/${memberId}`;

  return (dispatch) => {
    dispatch(showLoading(url));

    return destroy(url)
      .then(() => {
        dispatch(hideLoading(url));
      })
      .catch((error) => {
        const errorMessage = COMMON_ERRORS[getByKey(error, 'response.data.code')] || t('unknown_error');
        dispatch(danger(errorMessage));
        dispatch(hideLoading(url));
        throw error; // don't suppress error
      });
  };
}

export function inviteTeamMember(organizationId, payload, asAdmin = false) {
  const url = `${asAdmin ? ADMIN_ORGANIZATIONS : ORGANIZATIONS}/${organizationId}/members`;

  return (dispatch) => {
    dispatch(showLoading(url));

    return post(url, { data: payload })
      .then((response) => {
        const invitedUser = response.data;
        dispatch(hideLoading(url));

        return invitedUser;
      })
      .catch((error) => {
        const key = COMMON_ERRORS[getByKey(error, 'response.data.code')] || t('unknown_error');
        dispatch(danger(key));
        dispatch(hideLoading(url));
        throw error;
      });
  };
}

export const getActiveInvitations = (userId: number): ThunkAction => (
  async () => {
    const url = getActiveInvitation(userId);
    const response = await get(url);

    return response.data;
  }
);

export default createReducer<TeamMembersState, any>('teamMembers', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RECEIVE]: (state, { organizationId, data }) => ({
    ...state,
    mapByOrganizationId: {
      ...state.mapByOrganizationId,
      [organizationId]: sortOldestFirst(data)
    }
  }),
  [RESET]: (state, { organizationId }) => ({
    ...state,
    mapByOrganizationId: {
      ...state.mapByOrganizationId,
      [organizationId]: []
    }
  })
});

